import { decodeEventLog, decodeFunctionData } from "viem";

import { ABI_ENTRIES, LOG_ABIS } from "./tx-signatures";

import type { Address, Hex } from "viem";

// ---------------------------------------------------------------------------
// Token decimals for context-aware formatting
// ---------------------------------------------------------------------------

const TOKEN_DECIMALS: Record<string, number> = {
  WETH: 18,
  ETH: 18,
  USDC: 6,
  USDT: 6,
  DAI: 18,
  WBTC: 8,
  UNI: 18,
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DecodedTx {
  protocol: "V2" | "V3" | "V4" | "ERC20" | "ERC-8004" | "Vault" | "Unknown";
  action: string;
  details: string;
}

export interface DecodedLog {
  eventName: string;
  args: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Transaction calldata decoder
// ---------------------------------------------------------------------------

export function decodeTx(
  input: Hex,
  to: Address,
  knownAddresses: Record<Address, string>,
): DecodedTx {
  if (input === "0x" || input.length < 10) {
    return { protocol: "Unknown", action: "Transfer ETH", details: "" };
  }

  for (const entry of ABI_ENTRIES) {
    try {
      const { functionName, args } = decodeFunctionData({
        abi: entry.abi as Parameters<typeof decodeFunctionData>[0]["abi"],
        data: input,
      });
      const { action, details } = entry.formatAction(
        functionName,
        args,
        knownAddresses,
        to,
      );
      return { protocol: entry.protocol, action, details };
    } catch {
      // ABI didn't match, try next
    }
  }

  // Fallback: show selector
  const selector = input.slice(0, 10);
  return { protocol: "Unknown", action: selector, details: "" };
}

// ---------------------------------------------------------------------------
// Event log decoder
// ---------------------------------------------------------------------------

export function tryDecodeLogs(
  logs: { topics: Hex[]; data: Hex }[],
): DecodedLog[] {
  const decoded: DecodedLog[] = [];
  for (const log of logs) {
    if (log.topics.length === 0) continue;
    for (const abi of LOG_ABIS) {
      try {
        const result = decodeEventLog({
          abi: abi as readonly unknown[],
          topics: log.topics as [Hex, ...Hex[]],
          data: log.data,
        });
        if (result.eventName) {
          const args: Record<string, unknown> = {};
          if (Array.isArray(result.args)) {
            result.args.forEach((v, i) => {
              args[String(i)] = v;
            });
          } else {
            Object.assign(args, result.args);
          }
          decoded.push({ eventName: result.eventName, args });
          break;
        }
      } catch {
        // Try next ABI
      }
    }
  }
  return decoded;
}

export function formatLogArg(value: unknown): string {
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "string" && value.length === 42)
    return `${value.slice(0, 8)}...${value.slice(-4)}`;
  return String(value);
}

// ---------------------------------------------------------------------------
// Context-aware log argument formatting
// ---------------------------------------------------------------------------

export interface DecodedCalldata {
  functionName: string;
  args: Record<string, string>;
}

/**
 * Format a log argument with contextual awareness:
 * - Resolves addresses to known labels
 * - Formats bigint amounts using token decimals when the arg name implies a value
 */
export function formatLogArgWithContext(
  argName: string,
  value: unknown,
  knownAddresses: Record<Address, string>,
  emittingContract?: string,
): string {
  // Resolve addresses to labels
  if (typeof value === "string" && value.length === 42) {
    // Zero address detection
    if (value === "0x0000000000000000000000000000000000000000") {
      return "Zero Address";
    }
    const lower = value.toLowerCase() as Address;
    for (const [addr, name] of Object.entries(knownAddresses)) {
      if (addr.toLowerCase() === lower) return name;
    }
    return `${value.slice(0, 8)}\u2026${value.slice(-4)}`;
  }

  // Format bigint amounts with token-aware decimals
  if (typeof value === "bigint") {
    const amountArgs = new Set([
      "value",
      "amount",
      "wad",
      "amount0",
      "amount1",
      "amountIn",
      "amountOut",
      "amount0In",
      "amount1In",
      "amount0Out",
      "amount1Out",
    ]);
    if (amountArgs.has(argName)) {
      if (emittingContract) {
        const decimals = TOKEN_DECIMALS[emittingContract];
        if (decimals !== undefined) {
          const num = Number(value) / 10 ** decimals;
          if (num === 0) return "0";
          if (Math.abs(num) < 0.0001) return num.toExponential(2);
          return num.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: Math.abs(num) >= 1 ? 4 : 6,
          });
        }
      }
      // Fallback: if value looks like a token amount, try 18-decimal formatting
      const raw = Number(value);
      if (raw > 1e6) {
        const num = raw / 1e18;
        if (num >= 0.0001 && num < 1e12) {
          return num.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          });
        }
      }
    }
    return value.toString();
  }

  return String(value);
}

// ---------------------------------------------------------------------------
// Recursive calldata argument formatter (handles structs/tuples)
// ---------------------------------------------------------------------------

/**
 * Format a decoded calldata argument, recursively handling structs and arrays.
 * Fixes [object Object] for tuple params like exactInputSingle's params.
 */
export function formatCallArg(
  argName: string,
  value: unknown,
  knownAddresses: Record<Address, string>,
  emittingContract?: string,
): string {
  if (
    value !== null &&
    typeof value === "object" &&
    typeof value !== "function"
  ) {
    if (Array.isArray(value)) {
      return `[${value.map((v, i) => formatCallArg(String(i), v, knownAddresses, emittingContract)).join(", ")}]`;
    }
    const entries = Object.entries(value as Record<string, unknown>);
    const fields = entries
      .map(
        ([k, v]) =>
          `${k}: ${formatCallArg(k, v, knownAddresses, emittingContract)}`,
      )
      .join(", ");
    return `{ ${fields} }`;
  }
  return formatLogArgWithContext(
    argName,
    value,
    knownAddresses,
    emittingContract,
  );
}

// ---------------------------------------------------------------------------
// Calldata decoder (for expanded detail view)
// ---------------------------------------------------------------------------

/**
 * Attempt to decode raw calldata against known ABIs.
 * Returns structured function name + named args, or null if no ABI matches.
 */
export function decodeCalldata(
  input: Hex,
  knownAddresses: Record<Address, string>,
): DecodedCalldata | null {
  if (input === "0x" || input.length < 10) return null;

  for (const entry of ABI_ENTRIES) {
    try {
      const result = decodeFunctionData({
        abi: entry.abi as Parameters<typeof decodeFunctionData>[0]["abi"],
        data: input,
      });
      const args: Record<string, string> = {};
      const abiFunc = (entry.abi as readonly Record<string, unknown>[]).find(
        (item) =>
          (item as { type?: string; name?: string }).type === "function" &&
          (item as { name?: string }).name === result.functionName,
      ) as { inputs?: { name: string }[] } | undefined;

      if (abiFunc?.inputs) {
        const argsArray = result.args;
        for (let i = 0; i < abiFunc.inputs.length; i++) {
          const abiInput: { name: string } | undefined = abiFunc.inputs[i];
          if (!abiInput) continue;
          const paramName = abiInput.name || `arg${String(i)}`;
          args[paramName] = formatCallArg(
            paramName,
            argsArray[i],
            knownAddresses,
          );
        }
      }
      return { functionName: result.functionName, args };
    } catch {
      // ABI didn't match, try next
    }
  }
  return null;
}
